"use client";

import {
  useState,
  useEffect,
  useTransition,
  useRef,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import {
  CalendarCheck,
  CalendarPlus,
  Check,
  Circle,
  Flame,
  Loader2,
  Repeat2,
} from "lucide-react";
import { useUser, SignInButton } from "@clerk/nextjs";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  toggleAttendance,
  getAttendanceState,
  getAttendanceStatesForSlug,
  getAttendanceStreak,
  type AttendanceStateMap,
  type AttendanceStreakResult,
} from "@/lib/server-actions/attendanceRecordActions";
import Section from "@/components/article-shell/Section";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { MobileBottomToolRegistration } from "@/components/mobile-bottom-tools";
import { handleServerActionError } from "@/lib/serverActionUtils";
import { isSharedAttendanceSlug } from "@/lib/code-lang";

import {
  STUDY_PAGE_COMPLETED,
  notifyStudyScheduleChanged,
  readStudyTaskLink,
} from "@/lib/study-task-link";

import { recordStudyPage } from "@/lib/record-study-page";

const SHELL_SECTION_KEY = "ui2-sec-attend";

// 언어별 출석 표시용 짧은 라벨 (LanguageToggle headerLabel과 일치: C / Java / Python). (JCG-2658)
const CODE_LANG_LABELS: Record<string, string> = {
  c: "C",
  java: "Java",
  python: "Python",
};
function codeLangLabel(lang: string): string {
  return CODE_LANG_LABELS[lang] ?? lang.toUpperCase();
}

// html 루트의 data-code-langs(전환 가능한 언어 목록) / data-code-lang(현재 언어)을 구독하는 외부 스토어.
// LanguageToggle이 effect에서 늦게 세팅하는 race를 MutationObserver로 견고하게 흡수하고,
// useSyncExternalStore로 set-state-in-effect 없이 SSR/CSR mismatch를 피한다(JCG-2035 패턴, JCG-2658).
// 스냅샷은 참조 안정성을 위해 문자열("<langs>|<current>")로 반환하고 컴포넌트에서 파싱한다.
// 구분자 '|'는 언어 코드/쉼표로 join된 목록에 등장하지 않아 안전.
const CODE_LANG_SNAPSHOT_SEP = "|";
function subscribeCodeLang(callback: () => void) {
  const root = document.documentElement;
  const observer = new MutationObserver(callback);
  observer.observe(root, {
    attributes: true,
    attributeFilter: ["data-code-langs", "data-code-lang"],
  });
  return () => observer.disconnect();
}
function getCodeLangSnapshot() {
  const root = document.documentElement;
  return `${root.getAttribute("data-code-langs") ?? ""}${CODE_LANG_SNAPSHOT_SEP}${root.getAttribute("data-code-lang") ?? ""}`;
}
function getServerCodeLangSnapshot() {
  // 서버: 언어 목록/현재 언어 없음 → 단일 모드로 취급
  return CODE_LANG_SNAPSHOT_SEP;
}

interface AttendanceButtonProps {
  slug: string;
}

interface IconPosition {
  x: number;
  y: number;
}

const ICON_POSITION_KEY_DESKTOP = "attendance-button-position-desktop";
const ICON_POSITION_KEY_MOBILE = "attendance-button-position-mobile";
const DEFAULT_BOTTOM = 128; // bottom-32
const DEFAULT_RIGHT = 16; // right-4
const DRAG_THRESHOLD = 5;

function useIsDesktop() {
  // useSyncExternalStore로 set-state-in-effect 회피 (JCG-2035)
  return useSyncExternalStore(
    (callback) => {
      const mq = window.matchMedia("(min-width: 640px)");
      mq.addEventListener("change", callback);
      return () => mq.removeEventListener("change", callback);
    },
    () => window.matchMedia("(min-width: 640px)").matches,
    () => true
  );
}

/**
 * 이론/문제 페이지의 출석체크 플로팅 버튼.
 * - PostLayout 우측 하단에 떠있고, 드래그로 위치 이동 가능
 * - localStorage에 위치 저장 (desktop/mobile 분리 키)
 * - 로그인 시: 클릭으로 attendance_records INSERT/DELETE 토글
 * - 비로그인 시: 로그인 모달 표시
 */
export default function AttendanceButton({ slug }: AttendanceButtonProps) {
  const { isLoaded, isSignedIn } = useUser();
  const writePending = useRef(false);
  // 언어별 출석/회독 상태 맵(키: code_lang). 단일 모드는 '' 하나만 담는다.
  // 낙관적 업데이트도 이 맵의 "현재 언어" 항목만 갱신한다. (JCG-2348 회독 의미 유지, JCG-2658)
  const [statesByLang, setStatesByLang] = useState<AttendanceStateMap>({});
  // 초기(또는 언어 모드 전환 재조회) 로드 완료 여부. false 동안 현재 언어 attended===null 로 토글을 막는다.
  const [statesLoaded, setStatesLoaded] = useState(false);
  // 셸 출석 Section의 연속일/주간 스트립 데이터 (셸 분기에서만 사용)
  const [streakData, setStreakData] = useState<AttendanceStreakResult | null>(
    null
  );
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isPending, startTransition] = useTransition();
  // useSyncExternalStore로 SSR/CSR mismatch 없이 mount 여부 판단 (JCG-2035)
  const isMounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );

  // 3컬럼 셸(UI 2.0) 우측 사이드바 슬롯. 출석은 Free 포함 모든 로그인 사용자에게 개방(JCG-2811)이며,
  // ArticleShell 이 로그인 사용자에게만 이 슬롯을 렌더하므로 여기서는 등급 게이트 없이 슬롯 존재만 확인한다.
  const { inShell, attendanceSlot, refreshAttendance } = useArticleShellSlots();
  const [shellOpen, setShellOpen] = useState(true);

  // 셸 Section 접힘 상태 복원 (lazy init 금지 — hydration mismatch 회피, 마운트 후 effect에서 복원)
  useEffect(() => {
    const saved = localStorage.getItem(SHELL_SECTION_KEY);
    if (saved !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShellOpen(saved === "true");
    }
  }, []);

  const toggleShellOpen = useCallback(() => {
    setShellOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SHELL_SECTION_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // 셸 출석 Section 한정: 연속일/주간 스트립 갱신 (회차 전체 출석 기준이라 페이지 토글 후 재조회 필요)
  const refreshStreak = useCallback(async () => {
    if (!inShell || !isSignedIn) return;
    try {
      const data = await getAttendanceStreak();
      setStreakData(data);
    } catch (error) {
      if (handleServerActionError(error)) return;
      console.error("출석 연속일 조회 실패:", error);
    }
  }, [inShell, isSignedIn]);

  const isDesktop = useIsDesktop();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [iconPosition, setIconPosition] = useState<IconPosition | null>(null);
  const dragState = useRef({
    isDragging: false,
    hasMoved: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });

  // ── 언어 모드 구독 (다국어 코딩 페이지에서만 활성) ──────────────────────────────
  // 단일 언어/이론 페이지는 data-code-langs 미설정 → codeLangs=[] → 단일 모드로 기존과 100% 동일.
  const codeLangSnapshot = useSyncExternalStore(
    subscribeCodeLang,
    getCodeLangSnapshot,
    getServerCodeLangSnapshot
  );
  const { codeLangs, isMultiLang, currentLang, effectiveLang } = useMemo(() => {
    const sepIndex = codeLangSnapshot.indexOf(CODE_LANG_SNAPSHOT_SEP);
    const langsRaw = codeLangSnapshot.slice(0, sepIndex);
    const currentRaw = codeLangSnapshot.slice(sepIndex + 1);
    // 세 언어를 나란히 비교하는 페이지는 토글이 있어도 출석을 언어별로 나누지 않는다. 한 번
    // 읽으면 세 언어를 다 보므로 탭과 무관하게 1회 출석이다(단일 모드 = 기존 이론 페이지와 동일).
    // 토글 자체는 그대로 두고 여기서만 끊는다 — 그 페이지에 세 언어가 있는 건 사실이고,
    // data-code-langs 를 숨기면 언어 전환이 함께 망가진다. (JCG-3655)
    const langs =
      langsRaw && !isSharedAttendanceSlug(slug)
        ? langsRaw.split(",").filter(Boolean)
        : [];
    const multi = langs.length >= 2;
    // 다국어: 현재 선택 언어(목록에 있을 때) 아니면 첫 언어. 단일 모드: '' (언어 무관).
    const current = multi
      ? currentRaw && langs.includes(currentRaw)
        ? currentRaw
        : (langs[0] ?? "")
      : "";
    return {
      codeLangs: langs,
      isMultiLang: multi,
      currentLang: current,
      // 서버액션에 넘길 codeLang — 다국어면 현재 언어, 단일이면 '' (기존 계약)
      effectiveLang: multi ? current : "",
    };
  }, [codeLangSnapshot, slug]);

  // 현재 언어(effectiveLang) 기준 파생값. attended===null = 로드중(토글 막음, JCG-2348 가드).
  const currentState = statesByLang[effectiveLang];
  const attended: boolean | null = statesLoaded
    ? (currentState?.attended ?? false)
    : null;
  const reviewCount = currentState?.reviewCount ?? 0;

  // 출석 상태 로드 — 다국어면 전 언어 배치 조회(getAttendanceStatesForSlug), 단일이면 '' 단건.
  // isMultiLang 이 뒤늦게 true 로 바뀌면(LanguageToggle race) 재조회하며, 그 사이 statesLoaded=false 로
  // 되돌려 attended===null 가드를 복원한다(오조회 상태로 토글 시 DELETE 소실 방지, JCG-2348/2658).
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !slug) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStatesLoaded(false);
    (async () => {
      try {
        if (isMultiLang) {
          const map = await getAttendanceStatesForSlug(slug);
          if (!cancelled) {
            setStatesByLang(map);
            setStatesLoaded(true);
          }
        } else {
          const state = await getAttendanceState(slug);
          if (!cancelled) {
            setStatesByLang({ "": state });
            setStatesLoaded(true);
          }
        }
      } catch (error) {
        if (cancelled || handleServerActionError(error)) return;
        console.error("출석 상태 조회 실패:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, slug, isMultiLang]);

  // 셸 출석 Section 진입 시 연속일/주간 스트립 초기 로드 (셸 분기 + 로그인 한정)
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !inShell) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStreakData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getAttendanceStreak();
        if (!cancelled) setStreakData(data);
      } catch (error) {
        if (cancelled || handleServerActionError(error)) return;
        console.error("출석 연속일 조회 실패:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, inShell]);

  // 위치 복원 (디바이스 종류별 키 분리) — 외부 localStorage 동기화
  useEffect(() => {
    if (!isMounted) return;
    try {
      const key = isDesktop
        ? ICON_POSITION_KEY_DESKTOP
        : ICON_POSITION_KEY_MOBILE;
      const saved = localStorage.getItem(key);
      if (saved) {
        const pos: IconPosition = JSON.parse(saved);
        const btnSize = 56;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIconPosition({
          x: Math.min(Math.max(0, pos.x), window.innerWidth - btnSize),
          y: Math.min(Math.max(0, pos.y), window.innerHeight - btnSize),
        });
      } else {
        setIconPosition(null);
      }
    } catch {
      /* ignore */
    }
  }, [isDesktop, isMounted]);

  // 드래그 핸들러
  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    dragState.current = {
      isDragging: true,
      hasMoved: false,
      startX: clientX,
      startY: clientY,
      offsetX: clientX - rect.left,
      offsetY: clientY - rect.top,
    };
  }, []);

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    const state = dragState.current;
    if (!state.isDragging) return;
    const dx = clientX - state.startX;
    const dy = clientY - state.startY;
    if (
      !state.hasMoved &&
      Math.abs(dx) < DRAG_THRESHOLD &&
      Math.abs(dy) < DRAG_THRESHOLD
    )
      return;
    state.hasMoved = true;
    const btn = buttonRef.current;
    const btnW = btn?.offsetWidth ?? 56;
    const btnH = btn?.offsetHeight ?? 56;
    const newX = Math.min(
      Math.max(0, clientX - state.offsetX),
      window.innerWidth - btnW
    );
    const newY = Math.min(
      Math.max(0, clientY - state.offsetY),
      window.innerHeight - btnH
    );
    setIconPosition({ x: newX, y: newY });
  }, []);

  const handleToggleForLang = useCallback((lang: string) => {
    if (!isSignedIn) {
      setShowLoginModal(true);
      return;
    }

    // 언어별 상태를 모두 읽기 전에는 토글하지 않는다. 로드 전 미출석으로 가정하면
    // 기존 출석 행을 DELETE하며 누적 회독이 소실될 수 있다(JCG-2348).
    if (!statesLoaded || (isMultiLang && !codeLangs.includes(lang))) return;

    const studyLink = readStudyTaskLink(
      new URLSearchParams(window.location.search),
    );
    if (writePending.current) return;
    writePending.current = true;
    startTransition(async () => {
      try {
        const prev = statesByLang[lang];
        const next = !prev?.attended;
        // 낙관적 업데이트 — 누른 언어 항목만 갱신(출석 시 회독 1, 취소 시 0)
        setStatesByLang((m) => ({
          ...m,
          [lang]: { attended: next, reviewCount: next ? 1 : 0 },
        }));

        let result;
        try {
          result = next
            ? await recordStudyPage(slug, lang, "attendance", studyLink)
            : await toggleAttendance(slug, lang, studyLink?.examSessionId);
        } catch (error) {
          setStatesByLang((m) => ({
            ...m,
            [lang]: prev ?? { attended: false, reviewCount: 0 },
          }));
          if (handleServerActionError(error)) return;
          console.error("출석 토글 실패:", error);
          toast.error("출석 처리 중 오류가 발생했습니다.");
          return;
        }

        if (!result.success) {
          // 롤백
          setStatesByLang((m) => ({
            ...m,
            [lang]: prev ?? { attended: false, reviewCount: 0 },
          }));
          if (result.needsLogin) {
            setShowLoginModal(true);
          } else {
            toast.error(result.error);
          }
          return;
        }

        setStatesByLang((m) => ({
          ...m,
          [lang]: { attended: result.attended, reviewCount: result.reviewCount },
        }));
        // 회차 전체 기준 연속일/주간 스트립 갱신 (셸 분기 한정, 내부 가드로 no-op 처리)
        void refreshStreak();
        // 좌측 지도(TrailMap)가 현재 페이지 출석/회독 표시를 즉시 갱신하도록 신호(JCG-2065)
        refreshAttendance();
        notifyStudyScheduleChanged();
        if (result.attended) {
          window.dispatchEvent(
            new CustomEvent(STUDY_PAGE_COMPLETED, {
              detail: { slug, codeLang: lang, link: studyLink, schedule: "schedule" in result ? result.schedule : undefined },
            }),
          );
          toast.success("출석체크 완료! 🥔", {
            duration: 2500,
            icon: "✅",
          });
        } else {
          toast("출석을 취소했습니다.", { icon: "↩️", duration: 2000 });
        }
      } finally {
        writePending.current = false;
      }
    });
  }, [
    statesLoaded,
    statesByLang,
    isMultiLang,
    codeLangs,
    isSignedIn,
    slug,
    refreshStreak,
    refreshAttendance,
  ]);

  const handleToggle = useCallback(() => {
    handleToggleForLang(effectiveLang);
  }, [handleToggleForLang, effectiveLang]);

  // 회독 +1 — "한 번 더 공부했어요" 보조 액션. 첫 호출이면 출석+1회독. (JCG-2348)
  const handleIncrement = useCallback(() => {
    if (!isSignedIn) {
      setShowLoginModal(true);
      return;
    }

    const lang = effectiveLang;
    const studyLink = readStudyTaskLink(
      new URLSearchParams(window.location.search),
    );
    if (writePending.current) return;
    writePending.current = true;
    startTransition(async () => {
      try {
        const prev = statesByLang[lang];
        const prevReviewCount = prev?.reviewCount ?? 0;
        // 낙관적 업데이트 — 현재 언어 항목만 +1 (실패 시 prev 로 정확히 롤백)
        setStatesByLang((m) => ({
          ...m,
          [lang]: { attended: true, reviewCount: prevReviewCount + 1 },
        }));

        let result;
        try {
          result = await recordStudyPage(slug, lang, "review", studyLink);
        } catch (error) {
          setStatesByLang((m) => ({
            ...m,
            [lang]: prev ?? { attended: false, reviewCount: 0 },
          }));
          if (handleServerActionError(error)) return;
          console.error("회독 추가 실패:", error);
          toast.error("회독 추가 중 오류가 발생했습니다.");
          return;
        }

        if (!result.success) {
          // 롤백
          setStatesByLang((m) => ({
            ...m,
            [lang]: prev ?? { attended: false, reviewCount: 0 },
          }));
          if (result.needsLogin) {
            setShowLoginModal(true);
          } else {
            toast.error(result.error);
          }
          return;
        }

        // 서버 확정값으로 정정
        setStatesByLang((m) => ({
          ...m,
          [lang]: { attended: result.attended, reviewCount: result.reviewCount },
        }));
        void refreshStreak();
        refreshAttendance();
        notifyStudyScheduleChanged();
        window.dispatchEvent(
          new CustomEvent(STUDY_PAGE_COMPLETED, {
            detail: { slug, codeLang: lang, link: studyLink, schedule: "schedule" in result ? result.schedule : undefined },
          }),
        );
        toast.success(`${result.reviewCount}회독 완료! 🥔`, {
          duration: 2500,
          icon: "📚",
        });
      } finally {
        writePending.current = false;
      }
    });
  }, [
    statesByLang,
    effectiveLang,
    isSignedIn,
    slug,
    refreshStreak,
    refreshAttendance,
  ]);

  const handleToggleRef = useRef(handleToggle);
  useEffect(() => {
    handleToggleRef.current = handleToggle;
  }, [handleToggle]);

  const handleDragEnd = useCallback(() => {
    const state = dragState.current;
    if (state.isDragging && state.hasMoved) {
      const btn = buttonRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const pos = { x: rect.left, y: rect.top };
        setIconPosition(pos);
        const key = isDesktop
          ? ICON_POSITION_KEY_DESKTOP
          : ICON_POSITION_KEY_MOBILE;
        try {
          localStorage.setItem(key, JSON.stringify(pos));
        } catch {
          /* ignore */
        }
      }
    }
    dragState.current = { ...state, isDragging: false, hasMoved: false };
  }, [isDesktop]);

  // 터치 이벤트
  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return;
    const onTouchStart = (e: TouchEvent) => {
      handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchMove = (e: TouchEvent) => {
      handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
      if (dragState.current.hasMoved) e.preventDefault();
    };
    const onTouchEnd = () => {
      const wasDragged = dragState.current.hasMoved;
      handleDragEnd();
      if (!wasDragged) handleToggleRef.current();
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [handleDragStart, handleDragMove, handleDragEnd]);

  // 마우스 이벤트
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (dragState.current.isDragging)
        handleDragMove(e.clientX, e.clientY);
    };
    const onMouseUp = () => {
      if (!dragState.current.isDragging) return;
      const wasDragged = dragState.current.hasMoved;
      handleDragEnd();
      if (!wasDragged) handleToggleRef.current();
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [handleDragMove, handleDragEnd]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleDragStart(e.clientX, e.clientY);
    },
    [handleDragStart]
  );

  // Hydration 에러 방지
  if (!isMounted) return null;

  const isAttended = attended === true;

  // 로그인했지만 getAttendanceState 가 아직 안 끝나 attended===null 인 사이엔 토글을 막는다.
  // 이때 handleToggle 의 `const next = !attended` 가 `!null===true` 로 무조건 출석 분기 →
  // 서버 toggleAttendance 가 기존 행을 찾아 DELETE 하며 누적 회독이 소실되기 때문(JCG-2348).
  // 비로그인은 attended 가 null 이어도 로그인 모달을 띄워야 하므로 반드시 isSignedIn 으로 가드.
  const isStateLoading = isSignedIn === true && attended === null;
  // 토글(출석/취소) 비활성 조건 — 셸·플로팅·모바일 dock 공통.
  const isToggleDisabled = isPending || !isLoaded || isStateLoading;

  // 다국어 모드에서만 현재 언어를 드러낸다(단일 모드는 "" → 기존 문구/픽셀 동일). (JCG-2658)
  const langLabelText = isMultiLang && currentLang ? codeLangLabel(currentLang) : "";
  const langSuffix = langLabelText ? ` · ${langLabelText}` : "";
  // 셸/플로팅 버튼 표시 텍스트 (다국어면 "출석 완료 · Java" 형태)
  const toggleText = `${isAttended ? "출석 완료" : "출석체크"}${langSuffix}`;

  // 셸/플로팅 분기 공통 접근성 라벨 (동일 3항 값 + 다국어면 현재 언어)
  const label =
    (!isSignedIn
      ? "출석체크 (로그인 필요)"
      : isAttended
        ? "출석체크 완료 (다시 누르면 취소)"
        : "출석체크") + langSuffix;

  const renderLanguageChips = (mobile: boolean) => (
    <ul
      role="list"
      aria-label="언어별 출석체크"
      className={cn("flex flex-wrap items-center gap-1", mobile ? "gap-2" : "mb-2")}
    >
      {codeLangs.map((lang) => {
        const done = statesLoaded && statesByLang[lang]?.attended === true;
        const languageName = lang === "c" ? "C언어" : codeLangLabel(lang);
        const actionLabel = done ? "출석 취소" : "출석체크";
        return (
          <li key={lang}>
            <button
              type="button"
              onClick={() => handleToggleForLang(lang)}
              disabled={isToggleDisabled}
              aria-label={`${languageName} ${actionLabel}`}
              aria-pressed={done}
              title={`${languageName} ${actionLabel}`}
              className={cn(
                "inline-flex touch-manipulation select-none items-center justify-center gap-1 rounded-full border font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                mobile ? "min-h-11 min-w-11 px-3 py-2 text-sm" : "px-2 py-0.5 text-[11px]",
                done
                  ? "border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 dark:hover:bg-emerald-950"
                  : "border-border bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                isToggleDisabled && "cursor-not-allowed opacity-50",
              )}
            >
              <span aria-hidden translate="no">{codeLangLabel(lang)}</span>
              {statesLoaded ? (
                done ? (
                  <Check className="h-3 w-3" aria-hidden strokeWidth={3} />
                ) : (
                  <Circle className="h-3 w-3" aria-hidden strokeWidth={2.5} />
                )
              ) : (
                <Loader2 className="h-3 w-3 motion-safe:animate-spin" aria-hidden />
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );

  // 3컬럼 셸 안: 우측 사이드바 슬롯에 접이식 Section 으로 출석 portal.
  // 출석 슬롯은 ArticleShell 이 로그인 사용자(Free 포함)에게만 렌더하므로(JCG-2811) 슬롯 존재만으로 충분.
  // 드래그/fixed/z 없는 일반 버튼 — 가시성은 Section 의 open 이 담당.
  if (inShell && attendanceSlot) {
    // 연속일 배지 (1일 이상일 때만 헤더에 노출)
    const streakBadge =
      streakData && streakData.streak > 0 ? (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
          <Flame className="h-3 w-3" aria-hidden strokeWidth={2.5} />
          {streakData.streak}일
        </span>
      ) : undefined;

    return createPortal(
      <>
        <Section
          icon={CalendarCheck}
          label="출석"
          amber
          open={shellOpen}
          onToggle={toggleShellOpen}
          right={streakBadge}
        >
          {/* ── 이 페이지(단계) 출석·회독 영역 — 7일 스트립과 분리해 "현재 페이지"임을 명확히 ── */}
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="min-w-0 truncate text-[11px] font-bold text-foreground">
              이 페이지
            </span>
            {isAttended && reviewCount >= 1 && (
              <span
                className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-emerald-300 bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                aria-label={`이 페이지 ${reviewCount}회독`}
                title={`이 페이지 ${reviewCount}회독`}
              >
                <Repeat2 className="h-3 w-3" aria-hidden strokeWidth={2.5} />
                {reviewCount}회독
              </span>
            )}
          </div>

          {/* 언어별 칩은 해당 언어의 출석/취소를 직접 토글한다. */}
          {isMultiLang && renderLanguageChips(false)}

          <button
            type="button"
            onClick={handleToggle}
            disabled={isToggleDisabled}
            aria-label={label}
            title={label}
            aria-pressed={isAttended}
            className={cn(
              "inline-flex w-full select-none items-center justify-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-semibold transition-colors duration-200",
              isAttended
                ? "border-emerald-600 bg-emerald-500 text-white hover:bg-emerald-600 dark:border-emerald-500 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                : "border-emerald-400 bg-card text-emerald-700 hover:border-emerald-500 hover:bg-emerald-50 dark:border-emerald-600 dark:text-emerald-300 dark:hover:bg-emerald-950/40",
              isPending && "cursor-wait opacity-70",
              (!isLoaded || isStateLoading) && "opacity-50",
            )}
          >
            {isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : isAttended ? (
              <CalendarCheck className="h-5 w-5" aria-hidden strokeWidth={2.5} />
            ) : (
              <CalendarPlus className="h-5 w-5" aria-hidden strokeWidth={2.5} />
            )}
            <span>{toggleText}</span>
          </button>

          {/* 회독 누적(+1) 보조 버튼 — 출석 완료 후 노출. emerald 토글과 구분되는 outline 보조 스타일. */}
          {isAttended && (
            <button
              type="button"
              onClick={handleIncrement}
              disabled={isPending || !isLoaded}
              aria-label={`회독 1회 추가 (${langLabelText ? `${langLabelText} ` : ""}현재 ${reviewCount}회독)`}
              title="한 번 더 공부했어요 (+1)"
              className={cn(
                "mt-2 inline-flex w-full select-none items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-card px-3 py-2 text-[13px] font-semibold text-emerald-700 transition-colors duration-200 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40",
                isPending && "cursor-wait opacity-70",
                !isLoaded && "opacity-50",
              )}
            >
              <Repeat2 className="h-4 w-4" aria-hidden strokeWidth={2.5} />
              <span>한 번 더 공부했어요 (+1)</span>
            </button>
          )}

          {/* ── 세션 전역 최근 7일 스트립 — "이 페이지"가 아니라 "날짜별 전체 출석"임을 라벨/구분선으로 분리 ── */}
          {streakData && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="mb-1.5 flex items-baseline justify-between gap-2">
                <span className="min-w-0 truncate text-[11px] font-bold text-foreground">
                  최근 7일 · 전체 학습
                </span>
                <span className="shrink-0 whitespace-nowrap text-[10px] font-medium text-muted-foreground">
                  날짜별 출석
                </span>
              </div>
              <ul
                role="list"
                aria-label={
                  streakData.streak > 0
                    ? `최근 7일 전체 출석 현황(날짜 단위), ${streakData.streak}일 연속 출석 중`
                    : "최근 7일 전체 출석 현황(날짜 단위)"
                }
                className="flex list-none justify-between gap-1"
              >
                {streakData.last7.map((day) => (
                  <li
                    key={day.date}
                    aria-label={`${day.date}${day.isToday ? " (오늘)" : ""} ${day.attended ? "출석함" : "미출석"}`}
                    className="flex flex-1 flex-col items-center gap-1"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "text-[10px] leading-none",
                        day.isToday
                          ? "font-bold text-amber-600 underline decoration-amber-400 decoration-2 underline-offset-2 dark:text-amber-400 dark:decoration-amber-500"
                          : "font-medium text-muted-foreground",
                      )}
                    >
                      {day.weekday}
                    </span>
                    <span
                      aria-hidden
                      className={cn(
                        "flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors",
                        day.attended
                          ? "border-amber-500 bg-amber-500 dark:border-amber-600 dark:bg-amber-600"
                          : "border-border bg-transparent",
                        day.isToday &&
                          "ring-2 ring-amber-400 ring-offset-1 ring-offset-card dark:ring-amber-500",
                      )}
                    >
                      {day.attended && (
                        <Check
                          className="h-3 w-3 text-white"
                          strokeWidth={3}
                          aria-hidden
                        />
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>로그인이 필요해요</DialogTitle>
              <DialogDescription>
                출석체크를 하려면 로그인이 필요합니다. 로그인하시면 학습 진행
                상황을 기록할 수 있어요.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowLoginModal(false)}
                className="rounded border border-border px-3 py-2 text-sm hover:bg-accent"
              >
                취소
              </button>
              <SignInButton mode="modal">
                <button
                  type="button"
                  className="rounded bg-emerald-500 px-3 py-2 text-sm text-white hover:bg-emerald-600"
                >
                  로그인
                </button>
              </SignInButton>
            </div>
          </DialogContent>
        </Dialog>
      </>,
      attendanceSlot,
    );
  }

  // 셸 안(데스크탑 글 페이지)에서 출석 슬롯이 없으면(비로그인·문제 풀이·비학습 글) 플로팅 버튼을
  // 그리지 않는다 — fixed 버튼이 셸 위에 겹치는 것을 방지(플로팅 폴백은 클래식/모바일 전용).
  if (inShell) {
    return null;
  }

  // 위치: 드래그된 적 있으면 inline left/top, 없으면 기본 bottom-32 right-4
  const positionStyle: React.CSSProperties = iconPosition
    ? { left: iconPosition.x, top: iconPosition.y }
    : { bottom: DEFAULT_BOTTOM, right: DEFAULT_RIGHT };

  // 단일 출석 모드의 모바일 dock 라벨은 기존처럼 회독 수를 드러낸다.
  // 다국어 모드는 같은 자리의 출석 버튼이 언어별 출석 패널을 연다.
  const mobileLangSuffix = langLabelText ? `·${langLabelText}` : "";
  const mobileToggleLabel =
    (isAttended
      ? reviewCount >= 1
        ? `${reviewCount}회독`
        : "출석완료"
      : "출석") + mobileLangSuffix;
  const mobileToggleAriaLabel =
    isAttended && reviewCount >= 1
      ? `${label} · 현재 ${reviewCount}회독`
      : label;

  const mobileBottomTool = (
    <>
      <MobileBottomToolRegistration
        tool={isMultiLang
          ? {
              id: "attendance",
              kind: "panel",
              label: "출석",
              icon: CalendarCheck,
              order: 20,
              ariaLabel: "언어별 출석 패널 열기",
              tone: "emerald",
              loading: !isLoaded || isStateLoading,
              pending: isPending,
              sheet: {
                title: "언어별 출석체크",
                description: "언어를 누르면 출석하고, 다시 누르면 취소합니다.",
                content: renderLanguageChips(true),
                closeOnContentInteraction: true,
              },
            }
          : {
              id: "attendance",
              kind: "action",
              label: mobileToggleLabel,
              icon: isAttended ? CalendarCheck : CalendarPlus,
              order: 20,
              ariaLabel: mobileToggleAriaLabel,
              tone: "emerald",
              selected: isAttended,
              // 로딩 중(state 미로드)엔 토글을 막아 누적 회독 DELETE 소실 방지(JCG-2348)
              loading: !isLoaded || isStateLoading,
              pending: isPending,
              onAction: handleToggle,
            }}
      />
      {/* 회독 +1 도구 — 출석 완료 상태일 때만 등록. 데스크탑 셸의 "한 번 더 공부했어요(+1)"와 동일 액션. */}
      {isAttended && (
        <MobileBottomToolRegistration
          tool={{
            id: "attendance-review",
            kind: "action",
            label: "+1 회독",
            icon: Repeat2,
            order: 21,
            ariaLabel: `회독 1회 추가 (${langLabelText ? `${langLabelText} ` : ""}현재 ${reviewCount}회독)`,
            tone: "emerald",
            loading: !isLoaded,
            pending: isPending,
            onAction: handleIncrement,
          }}
        />
      )}
    </>
  );

  return (
    <>
      {mobileBottomTool}
      <button
        ref={buttonRef}
        type="button"
        onMouseDown={onMouseDown}
        disabled={isToggleDisabled}
        aria-label={label}
        title={label}
        aria-pressed={isAttended}
        style={positionStyle}
        className={cn(
          "fixed z-40 hidden select-none cursor-grab active:cursor-grabbing lg:inline-flex",
          "items-center gap-2 rounded-full transition-colors duration-200 border-2",
          "px-4 py-2.5 shadow-lg hover:shadow-xl",
          "text-sm font-semibold",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          isAttended
            ? "bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600 dark:bg-emerald-600 dark:border-emerald-500 dark:hover:bg-emerald-700"
            : "bg-white text-emerald-700 border-emerald-400 hover:bg-emerald-50 hover:border-emerald-500 dark:bg-gray-800 dark:text-emerald-300 dark:border-emerald-600 dark:hover:bg-emerald-950/40",
          isPending && "opacity-70 cursor-wait",
          (!isLoaded || isStateLoading) && "opacity-50"
        )}
      >
        {isPending ? (
          <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
        ) : isAttended ? (
          <CalendarCheck className="w-5 h-5" aria-hidden strokeWidth={2.5} />
        ) : (
          <CalendarPlus className="w-5 h-5" aria-hidden strokeWidth={2.5} />
        )}
        <span>{toggleText}</span>
        {/* 회독 수 표시(출석 완료 시) — 흰 버튼 위 emerald pill 로 대비 확보. 셸 패널이 회독 누적의 1순위 수단. */}
        {isAttended && reviewCount >= 1 && (
          <span
            className="inline-flex items-center rounded-full bg-white px-1.5 text-[11px] font-bold leading-5 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200"
            aria-label={`${reviewCount}회독`}
            title={`${reviewCount}회독`}
          >
            {reviewCount}회독
          </span>
        )}
      </button>

      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>로그인이 필요해요</DialogTitle>
            <DialogDescription>
              출석체크를 하려면 로그인이 필요합니다. 로그인하시면 학습 진행
              상황을 기록할 수 있어요.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => setShowLoginModal(false)}
              className="px-3 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              취소
            </button>
            <SignInButton mode="modal">
              <button
                type="button"
                className="px-3 py-2 text-sm rounded bg-emerald-500 text-white hover:bg-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                로그인
              </button>
            </SignInButton>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
